// (b) MCQ input, badge-letter style (A/B/C/D). The correct option index is
// NEVER sent to the client, so there is nothing to leak here. Locks after
// submission. Shared by Practice, Scaffold, and the exam AnswerPanel.
import { useState } from 'react';
import { MathText } from '@/components/exam/MathText';

interface MCQInputProps {
  options: string[];
  disabled?: boolean;
  submitting?: boolean;
  onSubmit: (selectedIdx: number) => void;
}

export function MCQInput({ options, disabled, submitting, onSubmit }: MCQInputProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const canSubmit = !disabled && !submitting && selected !== null;

  return (
    <div>
      <fieldset disabled={disabled} className="flex flex-col gap-2.5">
        <legend className="sr-only">Select one option</legend>
        {options.map((opt, idx) => {
          const checked = selected === idx;
          return (
            <label
              key={idx}
              className="flex items-center gap-3.5 rounded-2xl border p-3.5 transition"
              style={{
                borderColor: checked ? 'var(--green-border)' : 'var(--border-default)',
                background: checked ? 'var(--green-bg)' : 'var(--surface-card)',
                opacity: disabled ? 0.7 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              <input
                type="radio"
                name="mcq"
                className="sr-only"
                checked={checked}
                disabled={disabled}
                onChange={() => setSelected(idx)}
              />
              <span
                className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-fs-13 font-extrabold"
                style={{
                  border: `1.5px solid ${checked ? 'var(--green-border)' : 'var(--border-strong)'}`,
                  background: checked ? 'var(--green-border)' : 'var(--surface-card)',
                  color: checked ? '#fff' : 'var(--text-hint)',
                }}
              >
                {'ABCD'[idx] ?? idx + 1}
              </span>
              <span className="flex-1 text-fs-15 font-medium" style={{ color: 'var(--text-body)' }}>
                <MathText>{opt}</MathText>
              </span>
            </label>
          );
        })}
      </fieldset>

      <button
        type="button"
        disabled={!canSubmit}
        onClick={() => selected !== null && onSubmit(selected)}
        className="mt-4 w-full rounded-2xl px-4 py-3 text-fs-15 font-bold transition"
        style={{
          background: canSubmit ? 'var(--action-bg)' : 'var(--line-strong)',
          color: '#fff',
          opacity: canSubmit ? 1 : 0.75,
          cursor: canSubmit ? 'pointer' : 'not-allowed',
        }}
      >
        {submitting ? 'Submitting…' : 'Submit answer'}
      </button>
    </div>
  );
}
